import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {CommonInput, CommonLabel, FlexBox, INPUT_TYPES, useBreakpoint, useToasts } from '@brg/ui';
import {  API, Endpoints, Lang, TeamAPI, Team } from '@brg/api';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useModal } from 'core';
import { FieldTitle, ModalButtonFilled, ModalButtonHollow, RadioContainer, RepostedText, Subtitle, Title, Wrapper } from './styles';
import { useProfile } from '@brg/auth';
import { getFindTab, OptionActiveT, OptionWithAddT, REPOST_MODE, RepostTabs } from './const';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectsGridActions } from 'shared/reducers/ProjectsGridSlice';
import { useGetProject } from 'shared/hooks/api/useGetProject';
import userChannelSlice from 'shared/reducers/UserChannelSlice';
import RadioButton from 'shared/components/Radio';
import { ITeamTab } from 'shared/pages/teams/components/TeamTabEdit';
import { ModerationType } from 'shared/pages/teams/const/ModerationTypes';
import { useRouteMatch } from 'react-router-dom';
import { useChannelsContext } from 'shared/contexts/ChannelsContext';
import { TeamStatusDrafts } from 'shared/helpers/teamsStatus';
import { ActiveRepostsList } from './component/ActiveRepostsList';

/**
 * @param Модалка репоста на канал @Artem
 */

export const RepostModal: FC<{
    projectId: string,
    callBack?: (flag?: boolean) => void,
    mode: REPOST_MODE
}> = ({
    projectId, 
    callBack,
    mode = REPOST_MODE.add
}) => {
    const { t, i18n: { language } } = useTranslation();
    const { info, error } = useToasts();
    const theme = useTheme();
    const profile = useProfile();
    const modal = useModal();
    const dispatch = useDispatch();
    //@ts-ignore
    const currentTeam = useSelector((state) => state?.organizations)?.team;
    const [time,setTime] = useState(Date.now());
    const {project} = useGetProject(projectId, time);
    const [activeTab, setActiveTab] = useState<RepostTabs>(RepostTabs.feed);
    const [selectedTabs, setSelectedTabs] = useState<OptionWithAddT[]>();

    const { 
        channels, 
        loading, 
        refetch,
        reset 
    } = useChannelsContext();

    const computeChannels = useMemo(
        () => channels?.filter(
            (channel) => {
                if (channel?.ownersIDs?.includes(profile?.accountId ?? '') && !TeamStatusDrafts(channel)) {
                    return true
                }

                return false
            })
    , [profile, channels])

    const hasOriginalValue = useMemo(() => {
        if (loading) return false;
        return getFindTab(computeChannels || [], projectId)
    } , [computeChannels, projectId, loading])

    const projectOnFeedOrChannel = useMemo(
        () => (!!project?.isInSub || !!hasOriginalValue ) 
    , [project, hasOriginalValue])

    const isOwner = useMemo(
        () => (currentTeam && profile && currentTeam?.ownersIDs?.includes(profile.accountId)) 
    , [profile, selectedTabs])

    const isChannel = useMemo(
        () =>  {
            return [ModerationType.Channel, ModerationType.ChannelGroupOrCourse]?.includes(currentTeam?.moderationType)
        }
    , [currentTeam, selectedTabs])

    const canUpdateChannelState = useMemo(() => {
        return isChannel && isOwner
    } , [isChannel, isOwner])

    const feedOption = useMemo(() => {
        return {
            label: t('repost.modal.feed_title'),
            value: 'feed',
            addOptions: []
        }
    } , [language])

    const placeholderOption = useMemo(() => {
        return {
            label: t('repost.modal.default_option'),
            value: ''
        }
    } , [language])

    const computeTabs = useMemo(() => {
        const tabs = computeChannels?.reduce<OptionWithAddT[]>((acc, { name, teamId, tabs }) => {
            return [
                ...acc,
                { 
                    label: Lang(name, language), 
                    value: teamId,
                    addOptions: tabs?.reduce(
                        (acc, { name, path }) => {
                        return [
                            ...acc,
                            { 
                                label: Lang(name, language), 
                                value: path,
                            }
                        ]
                    } ,[]) || []
                }
            ]
        } , [])

        return [feedOption, ...tabs || []]
    } , [computeChannels, language, time])

    const submitText = useMemo(() => {
        return t('repost.modal.buttons.apply')
    }, [computeTabs, project, activeTab, projectOnFeedOrChannel])

    useEffect(() => {
        return () => {
            dispatch(userChannelSlice.actions.reset())
        }
    } , [])

    const onCancel = () => {
        modal.hide();
    }

    const handleUpdateFeed = async(isDeleted?: boolean) => {
        if (!project) return;

        await API.request()
        .endpoint(Endpoints.subscriptions)
        .url(`/api/Subscription/${isDeleted ? 'CancelSubscription' : 'Subscribe'}`)
        .post()
        .body({
            subscriberId: profile?.accountId,
            subscriberType: 0,
            targetId: projectId,
            targetType: 'project',
        })
        .send()
        .then((response) => {
            if (response.status === 200) {
                info(t('repost.modal.msg.feed_update'));
            }

            if (canUpdateChannelState && isDeleted) {
                window.location.reload();
                // window.location.href = window.origin + '/teams/' + currentTeam.teamId
            }
            callBack?.(isDeleted ? false : true)
        })
    }

    const handleUpdateChannelTabs = async (data: Team<'Global'>, closable?: boolean) => {
        return await TeamAPI.Update(data)
            .then((response) => {
                if (response.status === 200) {
                    refetch?.()
    
                    if (canUpdateChannelState) {
                        dispatch(ProjectsGridActions.setTabs({
                            tabs: data?.tabs,
                            //@ts-ignore
                            categories: data?.categories || [],
                            mainEntityType: data.mainEntityType,
                            mainFilter: data?.mainFilter,
                            context: data.contextName,
                        }))
                    }

                    callBack?.(data?.tabs?.some(({ filter}) => filter?.projectIds?.includes(projectId)))

                    if (closable) {
                        return modal.hide()
                    }
                }
        })
    }

    const isActive = useCallback(( levelOneId: string, levelTwoId?: string ) => {
        const channel = computeChannels?.find((channel) => channel?.teamId === levelOneId)
        const tab = channel?.tabs?.find((tab) => tab?.path === levelTwoId)
        const hasProject = tab?.filter?.projectIds?.includes(projectId)
        const isSelected = selectedTabs?.find((item)  => {
            return item.value === levelOneId && item?.addOptions?.[0]?.value === levelTwoId
        })

        if (hasProject || isSelected) {
            return tab.path
        }
        return false;
    } , [computeChannels, projectId, selectedTabs])


    const disableSubmit = useMemo(() => {
        return !selectedTabs?.length
    } , [selectedTabs, activeTab])

    const onSubmit = () => {
        if (disableSubmit) return;
        const selected = selectedTabs?.map((tab) => tab?.value);
        const options = selectedTabs?.map((tab) => tab?.addOptions?.[0]?.value);
        const feed = selectedTabs?.find((i) => i?.value === 'feed')

        const channels = computeChannels?.reduce((acc, val) => {
            if (selected?.includes(val?.teamId)) {
                const newTabs = val?.tabs?.map((tab) => {
                    if (options?.includes(tab?.path)) {
                        return {
                            ...tab,
                            filter: {
                                ...tab?.filter,
                                projectIds: [
                                    ...(tab?.filter?.projectIds || []),
                                    projectId
                                ]
                            }
                        }
                    }

                    return tab
                })
                return [
                    ...acc,
                    {
                        ...val,
                        tabs: newTabs
                    }
                ]
            }
            return acc
        }, []) as any

        if (feed) {
            handleUpdateFeed().then((r) => modal.hide())
        }

        if (channels?.length) {
            Promise.all(channels?.map(async(channel: Team<'Global'>) => {   
                handleUpdateChannelTabs(channel, true) 
            } ))
            .then((r) => {
                info(t('repost.modal.msg.channel_update'))
            })
        }
    }

    const onRemove = useCallback((id: string, parentId: string) => {
        const isSelected = selectedTabs?.find((tab) =>{
            return tab?.addOptions?.[0]?.value === id || tab?.value === id
        }) 
        const isFeed = id === 'feed';

        if (isSelected) {
            setSelectedTabs(selectedTabs?.filter((item) => {
                if (isFeed) {
                    return item?.value !== 'feed'
                }
                return item?.addOptions?.[0]?.value !== id
            }))
            return;
        } 

        const channel = computeChannels?.find((channel) => {
            return channel?.teamId === parentId
        })

        const newTabs = channel?.tabs?.map((tab) => {
            if (tab.path === id) {
                return {
                    ...tab,
                    filter: {
                        ...tab?.filter,
                        projectIds: tab?.filter?.projectIds?.filter((item: string) => item !== projectId )
                    }
                }
            }
            return tab
        })

        if (isFeed) {
            handleUpdateFeed(true).then((r) => {
                setTime(Date.now())
            })
            return;
        }
        else if (channel) {
            handleUpdateChannelTabs({
                ...channel, 
                tabs: newTabs
            }).then((r) => {
                setTime(Date.now());
                info(t('repost.modal.msg.channel_update'));
            })
        }
    }, [selectedTabs, computeChannels, project, refetch, projectId])

    const activeOptions: OptionActiveT[] = useMemo(() => {
        const filteredOptions = computeTabs?.filter((item) => {
            if (item.value === 'feed' && (!!selectedTabs?.find((i) => i.value === 'feed') || !!project?.isInSub)) {
                return true
            }
            return item?.addOptions?.filter((option) => {
                return !!isActive(item.value, option.value)
            }) 
        }) as OptionWithAddT[]

        const calcOptions = filteredOptions?.map((item) => {
            return {
                ...item,
                addOptions: item?.addOptions?.filter((option) => {
                    return !!isActive(item.value, option?.value)
                }) || []
            }
        })
        
        return [...calcOptions || []].reduce((acc, item) => {
            const projectOnFeed = !!project?.isInSub || selectedTabs?.find((i) => i.value === 'feed');

            const transformedOptions = item.value === 'feed' && projectOnFeed ? [
                {
                    label: item.label,
                    value: item.value,
                    parent: null
                  
                }
            ] : item.addOptions.map(option => {
                return {
                    label: option.label,
                    value: option.value,
                    parent: {
                      label: item.label,
                      value: item.value
                    }
                  
                }
            });

            acc.push(...transformedOptions);
            return acc;
          }, []);
    } , [computeTabs, selectedTabs, project, time])


    const onChange = useCallback((channelId: string, tabId: string) => {
        const channel = computeChannels?.find((channel) => channel?.teamId === channelId)
        const tab = channel?.tabs?.find((tab) => tab.path === tabId);
        let newTab;

        const isSelected = !!activeOptions?.find(
            (item) => item?.value === tabId || item?.value === channelId
        )

        if (isSelected) {
            return
        }

        if (channel) {
            newTab = {
                label: Lang(channel.name, language),
                value: channel.teamId,
                addOptions: [
                    {
                        label: Lang(tab.name, language),
                        value: tab.path
                    }
                ]
            }
        } 
        if (channelId === 'feed') {
            newTab = {
                label: t('repost.modal.feed_title'),
                value: 'feed',
                addOptions: [
                    {
                        label: t('repost.modal.feed_title'),
                        value: 'feed',
                    }
                ]
            }
        }

        if (newTab) {
            setSelectedTabs([...selectedTabs || [], newTab])
        }

    } , [selectedTabs, computeChannels, language])

    const channelsList = useMemo(() => {

        return (
            <FlexBox direction='column'>
                <FieldTitle color={theme.approvalBlockStyles.color}>{t('repost.modal.channels_title')}</FieldTitle>
                <CommonInput 
                    type={INPUT_TYPES.level_dropdown}
                    orientation={'column'}
                    placeholder={t('repost.modal.channels_placeholder')}
                    value={placeholderOption}
                    options={computeTabs as any}
                    isActive={isActive}
                    //@ts-ignore
                    onChange={onChange}
                />
            </FlexBox>
        )
    } , [computeTabs, activeTab, selectedTabs, feedOption, placeholderOption, language])

    const activeReposts = useMemo(() => {
        if (activeOptions?.length) {
            return (
                <ActiveRepostsList 
                    array={activeOptions} 
                    onRemove={onRemove}
                />
            )
        }
    } , [activeOptions])

    const title = useMemo(() => {

        if (projectOnFeedOrChannel) {
            return (
                <Title color={theme.not_saved_modal.title}>
                {t('repost.modal.move')}
            </Title> 
            )
        }
        return (
            <Title color={theme.not_saved_modal.title}>
                {t('repost.modal.title')}
            </Title>
        )
    } , [mode, projectOnFeedOrChannel])

    useEffect(() => {
        return () => {
            reset?.()
        }
    } , [])


    return <Wrapper direction={'column'} width={'100%'} justify='left' align='center' gy={32}>
            {title}
            {channelsList}
            {activeReposts}
            <FlexBox width={'100%'} direction='row' directionMobile='column' gx={24} gxMobile={1} gy={1} gyMobile={8}>
                <ModalButtonHollow 
                    onClick={onCancel}
                >
                    {t('repost.modal.buttons.cancel')}
                </ModalButtonHollow>
                <ModalButtonFilled 
                    disabled={disableSubmit} 
                    onClick={onSubmit}
                >
                    {submitText}
                </ModalButtonFilled>
            </FlexBox>
        </Wrapper>

};
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {CommonInput, CommonLabel, FlexBox, INPUT_TYPES, useBreakpoint, useToasts } from '@brg/ui';
import {  API, Endpoints, Lang, TeamAPI, Team } from '@brg/api';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useModal } from 'core';
import { FieldTitle, ModalButtonFilled, ModalButtonHollow, RadioContainer, RepostedText, Subtitle, Title, Wrapper } from './styles';
import { useProfile } from '@brg/auth';
import { getFindTab, OptionActiveT, OptionWithAddT, REPOST_MODE, RepostTabs } from './const';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectsGridActions } from 'shared/reducers/ProjectsGridSlice';
import { useGetProject } from 'shared/hooks/api/useGetProject';
import userChannelSlice from 'shared/reducers/UserChannelSlice';
import RadioButton from 'shared/components/Radio';
import { ITeamTab } from 'shared/pages/teams/components/TeamTabEdit';
import { ModerationType } from 'shared/pages/teams/const/ModerationTypes';
import { useRouteMatch } from 'react-router-dom';
import { useChannelsContext } from 'shared/contexts/ChannelsContext';
import { TeamStatusDrafts } from 'shared/helpers/teamsStatus';
import { ActiveRepostsList } from './component/ActiveRepostsList';

/**
 * @param Модалка репоста на канал @Artem
 */

export const RepostModal: FC<{
    projectId: string,
    callBack?: (flag?: boolean) => void,
    mode: REPOST_MODE
}> = ({
    projectId, 
    callBack,
    mode = REPOST_MODE.add
}) => {
    const { t, i18n: { language } } = useTranslation();
    const { info, error } = useToasts();
    const theme = useTheme();
    const profile = useProfile();
    const modal = useModal();
    const dispatch = useDispatch();
    //@ts-ignore
    const currentTeam = useSelector((state) => state?.organizations)?.team;
    const [time,setTime] = useState(Date.now());
    const {project} = useGetProject(projectId, time);
    const [activeTab, setActiveTab] = useState<RepostTabs>(RepostTabs.feed);
    const [selectedTabs, setSelectedTabs] = useState<OptionWithAddT[]>();

    const { 
        channels, 
        loading, 
        refetch,
        reset 
    } = useChannelsContext();

    const computeChannels = useMemo(
        () => channels?.filter(
            (channel) => {
                if (channel?.ownersIDs?.includes(profile?.accountId ?? '') && !TeamStatusDrafts(channel)) {
                    return true
                }

                return false
            })
    , [profile, channels])

    const hasOriginalValue = useMemo(() => {
        if (loading) return false;
        return getFindTab(computeChannels || [], projectId)
    } , [computeChannels, projectId, loading])

    const projectOnFeedOrChannel = useMemo(
        () => (!!project?.isInSub || !!hasOriginalValue ) 
    , [project, hasOriginalValue])

    const isOwner = useMemo(
        () => (currentTeam && profile && currentTeam?.ownersIDs?.includes(profile.accountId)) 
    , [profile, selectedTabs])

    const isChannel = useMemo(
        () =>  {
            return [ModerationType.Channel, ModerationType.ChannelGroupOrCourse]?.includes(currentTeam?.moderationType)
        }
    , [currentTeam, selectedTabs])

    const canUpdateChannelState = useMemo(() => {
        return isChannel && isOwner
    } , [isChannel, isOwner])

    const feedOption = useMemo(() => {
        return {
            label: t('repost.modal.feed_title'),
            value: 'feed',
            addOptions: []
        }
    } , [language])

    const placeholderOption = useMemo(() => {
        return {
            label: t('repost.modal.default_option'),
            value: ''
        }
    } , [language])

    const computeTabs = useMemo(() => {
        const tabs = computeChannels?.reduce<OptionWithAddT[]>((acc, { name, teamId, tabs }) => {
            return [
                ...acc,
                { 
                    label: Lang(name, language), 
                    value: teamId,
                    addOptions: tabs?.reduce(
                        (acc, { name, path }) => {
                        return [
                            ...acc,
                            { 
                                label: Lang(name, language), 
                                value: path,
                            }
                        ]
                    } ,[]) || []
                }
            ]
        } , [])

        return [feedOption, ...tabs || []]
    } , [computeChannels, language, time])

    const submitText = useMemo(() => {
        return t('repost.modal.buttons.apply')
    }, [computeTabs, project, activeTab, projectOnFeedOrChannel])

    useEffect(() => {
        return () => {
            dispatch(userChannelSlice.actions.reset())
        }
    } , [])

    const onCancel = () => {
        modal.hide();
    }

    const handleUpdateFeed = async(isDeleted?: boolean) => {
        if (!project) return;

        await API.request()
        .endpoint(Endpoints.subscriptions)
        .url(`/api/Subscription/${isDeleted ? 'CancelSubscription' : 'Subscribe'}`)
        .post()
        .body({
            subscriberId: profile?.accountId,
            subscriberType: 0,
            targetId: projectId,
            targetType: 'project',
        })
        .send()
        .then((response) => {
            if (response.status === 200) {
                info(t('repost.modal.msg.feed_update'));
            }

            if (canUpdateChannelState && isDeleted) {
                window.location.reload();
                // window.location.href = window.origin + '/teams/' + currentTeam.teamId
            }
            callBack?.(isDeleted ? false : true)
        })
    }

    const handleUpdateChannelTabs = async (data: Team<'Global'>, closable?: boolean) => {
        return await TeamAPI.Update(data)
            .then((response) => {
                if (response.status === 200) {
                    refetch?.()
    
                    if (canUpdateChannelState) {
                        dispatch(ProjectsGridActions.setTabs({
                            tabs: data?.tabs,
                            //@ts-ignore
                            categories: data?.categories || [],
                            mainEntityType: data.mainEntityType,
                            mainFilter: data?.mainFilter,
                            context: data.contextName,
                        }))
                    }

                    callBack?.(data?.tabs?.some(({ filter}) => filter?.projectIds?.includes(projectId)))

                    if (closable) {
                        return modal.hide()
                    }
                }
        })
    }

    const isActive = useCallback(( levelOneId: string, levelTwoId?: string ) => {
        const channel = computeChannels?.find((channel) => channel?.teamId === levelOneId)
        const tab = channel?.tabs?.find((tab) => tab?.path === levelTwoId)
        const hasProject = tab?.filter?.projectIds?.includes(projectId)
        const isSelected = selectedTabs?.find((item)  => {
            return item.value === levelOneId && item?.addOptions?.[0]?.value === levelTwoId
        })

        if (hasProject || isSelected) {
            return tab.path
        }
        return false;
    } , [computeChannels, projectId, selectedTabs])


    const disableSubmit = useMemo(() => {
        return !selectedTabs?.length
    } , [selectedTabs, activeTab])

    const onSubmit = () => {
        if (disableSubmit) return;
        const selected = selectedTabs?.map((tab) => tab?.value);
        const options = selectedTabs?.map((tab) => tab?.addOptions?.[0]?.value);
        const feed = selectedTabs?.find((i) => i?.value === 'feed')

        const channels = computeChannels?.reduce((acc, val) => {
            if (selected?.includes(val?.teamId)) {
                const newTabs = val?.tabs?.map((tab) => {
                    if (options?.includes(tab?.path)) {
                        return {
                            ...tab,
                            filter: {
                                ...tab?.filter,
                                projectIds: [
                                    ...(tab?.filter?.projectIds || []),
                                    projectId
                                ]
                            }
                        }
                    }

                    return tab
                })
                return [
                    ...acc,
                    {
                        ...val,
                        tabs: newTabs
                    }
                ]
            }
            return acc
        }, []) as any

        if (feed) {
            handleUpdateFeed().then((r) => modal.hide())
        }

        if (channels?.length) {
            Promise.all(channels?.map(async(channel: Team<'Global'>) => {   
                handleUpdateChannelTabs(channel, true) 
            } ))
            .then((r) => {
                info(t('repost.modal.msg.channel_update'))
            })
        }
    }

    const onRemove = useCallback((id: string, parentId: string) => {
        const isSelected = selectedTabs?.find((tab) =>{
            return tab?.addOptions?.[0]?.value === id || tab?.value === id
        }) 
        const isFeed = id === 'feed';

        if (isSelected) {
            setSelectedTabs(selectedTabs?.filter((item) => {
                if (isFeed) {
                    return item?.value !== 'feed'
                }
                return item?.addOptions?.[0]?.value !== id
            }))
            return;
        } 

        const channel = computeChannels?.find((channel) => {
            return channel?.teamId === parentId
        })

        const newTabs = channel?.tabs?.map((tab) => {
            if (tab.path === id) {
                return {
                    ...tab,
                    filter: {
                        ...tab?.filter,
                        projectIds: tab?.filter?.projectIds?.filter((item: string) => item !== projectId )
                    }
                }
            }
            return tab
        })

        if (isFeed) {
            handleUpdateFeed(true).then((r) => {
                setTime(Date.now())
            })
            return;
        }
        else if (channel) {
            handleUpdateChannelTabs({
                ...channel, 
                tabs: newTabs
            }).then((r) => {
                setTime(Date.now());
                info(t('repost.modal.msg.channel_update'));
            })
        }
    }, [selectedTabs, computeChannels, project, refetch, projectId])

    const activeOptions: OptionActiveT[] = useMemo(() => {
        const filteredOptions = computeTabs?.filter((item) => {
            if (item.value === 'feed' && (!!selectedTabs?.find((i) => i.value === 'feed') || !!project?.isInSub)) {
                return true
            }
            return item?.addOptions?.filter((option) => {
                return !!isActive(item.value, option.value)
            }) 
        }) as OptionWithAddT[]

        const calcOptions = filteredOptions?.map((item) => {
            return {
                ...item,
                addOptions: item?.addOptions?.filter((option) => {
                    return !!isActive(item.value, option?.value)
                }) || []
            }
        })
        
        return [...calcOptions || []].reduce((acc, item) => {
            const projectOnFeed = !!project?.isInSub || selectedTabs?.find((i) => i.value === 'feed');

            const transformedOptions = item.value === 'feed' && projectOnFeed ? [
                {
                    label: item.label,
                    value: item.value,
                    parent: null
                  
                }
            ] : item.addOptions.map(option => {
                return {
                    label: option.label,
                    value: option.value,
                    parent: {
                      label: item.label,
                      value: item.value
                    }
                  
                }
            });

            acc.push(...transformedOptions);
            return acc;
          }, []);
    } , [computeTabs, selectedTabs, project, time])


    const onChange = useCallback((channelId: string, tabId: string) => {
        const channel = computeChannels?.find((channel) => channel?.teamId === channelId)
        const tab = channel?.tabs?.find((tab) => tab.path === tabId);
        let newTab;

        const isSelected = !!activeOptions?.find(
            (item) => item?.value === tabId || item?.value === channelId
        )

        if (isSelected) {
            return
        }

        if (channel) {
            newTab = {
                label: Lang(channel.name, language),
                value: channel.teamId,
                addOptions: [
                    {
                        label: Lang(tab.name, language),
                        value: tab.path
                    }
                ]
            }
        } 
        if (channelId === 'feed') {
            newTab = {
                label: t('repost.modal.feed_title'),
                value: 'feed',
                addOptions: [
                    {
                        label: t('repost.modal.feed_title'),
                        value: 'feed',
                    }
                ]
            }
        }

        if (newTab) {
            setSelectedTabs([...selectedTabs || [], newTab])
        }

    } , [selectedTabs, computeChannels, language])

    const channelsList = useMemo(() => {

        return (
            <FlexBox direction='column'>
                <FieldTitle color={theme.approvalBlockStyles.color}>{t('repost.modal.channels_title')}</FieldTitle>
                <CommonInput 
                    type={INPUT_TYPES.level_dropdown}
                    orientation={'column'}
                    placeholder={t('repost.modal.channels_placeholder')}
                    value={placeholderOption}
                    options={computeTabs as any}
                    isActive={isActive}
                    //@ts-ignore
                    onChange={onChange}
                />
            </FlexBox>
        )
    } , [computeTabs, activeTab, selectedTabs, feedOption, placeholderOption, language])

    const activeReposts = useMemo(() => {
        if (activeOptions?.length) {
            return (
                <ActiveRepostsList 
                    array={activeOptions} 
                    onRemove={onRemove}
                />
            )
        }
    } , [activeOptions])

    const title = useMemo(() => {

        if (projectOnFeedOrChannel) {
            return (
                <Title color={theme.not_saved_modal.title}>
                {t('repost.modal.move')}
            </Title> 
            )
        }
        return (
            <Title color={theme.not_saved_modal.title}>
                {t('repost.modal.title')}
            </Title>
        )
    } , [mode, projectOnFeedOrChannel])

    useEffect(() => {
        return () => {
            reset?.()
        }
    } , [])


    return <Wrapper direction={'column'} width={'100%'} justify='left' align='center' gy={32}>
            {title}
            {channelsList}
            {activeReposts}
            <FlexBox width={'100%'} direction='row' directionMobile='column' gx={24} gxMobile={1} gy={1} gyMobile={8}>
                <ModalButtonHollow 
                    onClick={onCancel}
                >
                    {t('repost.modal.buttons.cancel')}
                </ModalButtonHollow>
                <ModalButtonFilled 
                    disabled={disableSubmit} 
                    onClick={onSubmit}
                >
                    {submitText}
                </ModalButtonFilled>
            </FlexBox>
        </Wrapper>

};
